import { useTheme } from '../../../hooks/useTheme';
import { useStyleSheet } from '../../../hooks/useStyleSheet';
import { ButtonStylesProps } from './Button.types';
import { getAccessibleButtonColors, getButtonSize } from './Button.helpers';
import { useButtonColors } from './hooks';

export const useStyles = ({
  typographyVariant,
  color,
  size,
  fullWidth,
  isActive,
  disabled,
  boxShadowVariant,
}: ButtonStylesProps) => {
  const theme = useTheme();

  const buttonColors = useButtonColors(color);
  const accessibleButtonColors = getAccessibleButtonColors(color, theme);
  const buttonSize = getButtonSize(size, theme);

  const styles = useStyleSheet(
    (theme) => {
      return {
        container: {
          base: {
            height: buttonSize.height,
            padding: buttonSize.padding,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            display: 'flex',
            gap: theme.spacing.secondary,
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: fullWidth ? '100%' : buttonSize.width,
            borderColor: 'transparent',
            textAlign: 'center',
            backgroundColor: isActive ? buttonColors.dark : buttonColors.main,
            transition: `all ${theme.transitions.primary}`,
            border: 'none',
            borderRadius: buttonSize.borderRadius,
            color: accessibleButtonColors.rest,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? '0.5' : undefined,
          },
          variants: {
            link: {
              color: buttonColors.main,
              '&:hover': {
                color: !disabled ? buttonColors.light : undefined,
              },
              '&:active': {
                color: !disabled ? buttonColors.dark : undefined,
              },
            },
            text: {
              display: 'flex',
              justifySelf: 'flex-start',
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              color: buttonColors.main,
              padding: 0,
              minWidth: 'initial',
              height: 'auto',
              borderRadius: '0',
              '&:hover': {
                color: !disabled ? buttonColors.light : undefined,
              },
              '&:active': {
                color: !disabled ? buttonColors.dark : undefined,
              },
            },
            contained: {
              boxShadow:
                !disabled && isActive
                  ? theme.boxShadows[boxShadowVariant].inset
                  : theme.boxShadows[boxShadowVariant].main,
              '&:hover': {
                backgroundColor: !disabled ? buttonColors.light : undefined,
                color: !disabled ? accessibleButtonColors.hover : undefined,
              },
              '&:active': {
                boxShadow: !disabled ? theme.boxShadows[boxShadowVariant].inset : undefined,
                backgroundColor: !disabled ? buttonColors.dark : undefined,
                color: !disabled ? accessibleButtonColors.active : undefined,
              },
            },
            outlined: {
              border: `1px solid ${buttonColors.main}`,
              boxShadow: 'none',
              '&:hover': {
                borderColor: !disabled ? buttonColors.light : undefined,
                backgroundColor: !disabled ? buttonColors.light : undefined,
              },
              '&:active': {
                borderColor: !disabled ? buttonColors.dark : undefined,
                backgroundColor: !disabled ? buttonColors.dark : undefined,
              },
            },
          },
        },
      };
    },
    [typographyVariant, color, size, fullWidth, isActive, disabled, boxShadowVariant],
  );

  return styles;
};
